const PROMOTIX_LOWEST_PRICE_PILL_ATTR = 'data-promotix-lowest-price-pill';
const PROMOTIX_LOWEST_PRICE_ROW_ATTR = 'data-promotix-lowest-price-row';
const PROMOTIX_LOWEST_PRICE_SLOT_CLASS = 'promotix-lowest-price-slot';

function parseMoney(value) {
  const amount = Number(String(value || '').replace(/,/g, ''));
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function parseRowPrice(row) {
  if (!(row instanceof HTMLElement)) return null;

  const dataPrice = parseMoney(row.getAttribute('data-price'));
  if (dataPrice != null) return dataPrice;

  const amtEl = row.querySelector('.venue-ticket-list-cta-amt, [class*="cta-amt"]');
  if (amtEl) {
    const match = (amtEl.textContent || '').match(/\$\s*(\d{1,4}(?:,\d{3})*(?:\.\d{2})?)/);
    if (match) return parseMoney(match[1]);
  }

  const priceCells = row.querySelectorAll('td, [class*="price"]');
  for (const cell of priceCells) {
    const text = cell.textContent || '';
    if (!/\$\s*\d/.test(text)) continue;
    const match = text.match(/\$\s*(\d{1,4}(?:,\d{3})*(?:\.\d{2})?)/);
    if (match) return parseMoney(match[1]);
  }

  const text = row.textContent || '';
  const prices = [];
  for (const match of text.matchAll(/\$\s*(\d{1,4}(?:,\d{3})*(?:\.\d{2})?)/g)) {
    const value = parseMoney(match[1]);
    if (value != null && value >= 3 && value <= 5000) prices.push(value);
  }
  return prices.length > 0 ? Math.min(...prices) : null;
}

function ticketListRows(root) {
  const selectors = [
    'tr.venue-ticket-list-row-js',
    'tr.Sea-TicketRow',
    '.venue-ticket-list-row-js',
    'table[class*="venue-ticket-list"] tbody tr',
    '#tickets-table tr.Sea-TicketRow',
  ];
  const seen = new Set();
  const rows = [];
  for (const selector of selectors) {
    for (const row of root.querySelectorAll(selector)) {
      if (!(row instanceof HTMLElement) || seen.has(row)) continue;
      if (!/\$\s*\d/.test(row.textContent || '')) continue;
      seen.add(row);
      rows.push(row);
    }
  }
  return rows;
}

function priceInsertTarget(row) {
  const amt = row.querySelector('.venue-ticket-list-cta-amt, [class*="cta-amt"]');
  if (amt instanceof HTMLElement && amt.parentElement instanceof HTMLElement) {
    return { container: amt.parentElement, before: amt };
  }

  const tds = row.querySelectorAll('td');
  if (tds.length >= 2) {
    const priceTd = tds[tds.length - 2];
    if (priceTd instanceof HTMLElement) {
      const priceNode = Array.from(priceTd.childNodes).find(
        node =>
          node.nodeType === Node.TEXT_NODE
            ? /\$\s*\d/.test(node.textContent || '')
            : node instanceof HTMLElement && /\$\s*\d/.test(node.textContent || '')
      );
      return {
        container: priceTd,
        before: priceNode instanceof HTMLElement ? priceNode : priceTd.firstChild,
      };
    }
  }

  for (const el of row.querySelectorAll('span, div, p')) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.children.length > 0) continue;
    if (!/\$\s*\d/.test(el.textContent || '')) continue;
    const parent = el.parentElement;
    if (parent instanceof HTMLElement) {
      return { container: parent, before: el };
    }
  }

  return null;
}

function clearLowestPricePills(root) {
  root.querySelectorAll(`[${PROMOTIX_LOWEST_PRICE_PILL_ATTR}]`).forEach(el => el.remove());
  root.querySelectorAll(`.${PROMOTIX_LOWEST_PRICE_SLOT_CLASS}`).forEach(el => el.remove());
  root.querySelectorAll(`[${PROMOTIX_LOWEST_PRICE_ROW_ATTR}]`).forEach(row => {
    row.removeAttribute(PROMOTIX_LOWEST_PRICE_ROW_ATTR);
  });
}

function ensurePriceSlot(row) {
  const target = priceInsertTarget(row);
  if (!target?.container) return null;

  let slot = target.container.querySelector(`.${PROMOTIX_LOWEST_PRICE_SLOT_CLASS}`);
  if (slot) return slot;

  slot = document.createElement('span');
  slot.className = PROMOTIX_LOWEST_PRICE_SLOT_CLASS;
  slot.setAttribute('aria-hidden', 'true');

  if (target.before) {
    target.container.insertBefore(slot, target.before);
  } else {
    target.container.insertBefore(slot, target.container.firstChild);
  }

  return slot;
}

function addLowestPricePill(row) {
  const slot = ensurePriceSlot(row);
  if (!slot || slot.querySelector(`[${PROMOTIX_LOWEST_PRICE_PILL_ATTR}]`)) return;

  const pill = document.createElement('span');
  pill.className = 'promotix-lowest-price-pill';
  pill.setAttribute(PROMOTIX_LOWEST_PRICE_PILL_ATTR, '1');
  pill.textContent = 'Lowest Price';
  slot.appendChild(pill);
  row.setAttribute(PROMOTIX_LOWEST_PRICE_ROW_ATTR, '1');
}

function patchLowestPricePill(root) {
  const rows = ticketListRows(root);
  if (rows.length === 0) return;

  let bestPrice = null;
  let bestRow = null;

  for (const row of rows) {
    const price = parseRowPrice(row);
    if (price == null) continue;
    if (bestPrice == null || price < bestPrice) {
      bestPrice = price;
      bestRow = row;
    }
  }

  const existingPill = root.querySelector(`[${PROMOTIX_LOWEST_PRICE_PILL_ATTR}]`);
  const existingRow = existingPill?.closest(`[${PROMOTIX_LOWEST_PRICE_ROW_ATTR}]`);
  if (existingRow === bestRow && existingPill) {
    for (const row of rows) {
      ensurePriceSlot(row);
    }
    return;
  }

  clearLowestPricePills(root);

  for (const row of rows) {
    ensurePriceSlot(row);
  }

  if (bestRow) addLowestPricePill(bestRow);
}

/**
 * Tag the cheapest Seatics ticket row with a PromoTix "Lowest Price" pill.
 */
export function installLowestPricePillPatch(cancelled, rootIds = ['tn-maps', 'sea-mp']) {
  if (typeof document === 'undefined') return () => {};

  const patch = () => {
    if (cancelled()) return;
    for (const id of rootIds) {
      const root = document.getElementById(id);
      if (root) patchLowestPricePill(root);
    }
  };

  const observerRoot = document.getElementById(rootIds[0]) || document.body;
  let patchScheduled = false;
  const schedulePatch = () => {
    if (patchScheduled || cancelled()) return;
    patchScheduled = true;
    const run = () => {
      patchScheduled = false;
      patch();
    };
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  };

  const isOurNode = node => {
    if (!(node instanceof HTMLElement)) return false;
    return (
      node.matches(`[${PROMOTIX_LOWEST_PRICE_PILL_ATTR}], .${PROMOTIX_LOWEST_PRICE_SLOT_CLASS}`) ||
      Boolean(
        node.querySelector(`[${PROMOTIX_LOWEST_PRICE_PILL_ATTR}], .${PROMOTIX_LOWEST_PRICE_SLOT_CLASS}`)
      )
    );
  };

  const observer = new MutationObserver(mutations => {
    const onlyOurChanges = mutations.every(mutation =>
      [...mutation.addedNodes, ...mutation.removedNodes].every(
        node => !(node instanceof HTMLElement) || isOurNode(node)
      )
    );
    if (!onlyOurChanges) schedulePatch();
  });
  observer.observe(observerRoot, { childList: true, subtree: true });
  const interval = setInterval(patch, 1500);
  patch();

  return () => {
    observer.disconnect();
    clearInterval(interval);
    for (const id of rootIds) {
      const root = document.getElementById(id);
      if (root) clearLowestPricePills(root);
    }
  };
}

export const LOWEST_PRICE_PILL_CSS = `
#tn-maps tr.venue-ticket-list-row-js > td:nth-last-child(2),
#sea-mp tr.venue-ticket-list-row-js > td:nth-last-child(2) {
  overflow: visible !important;
}

.promotix-lowest-price-slot {
  display: inline-block;
  width: 76px;
  margin-right: 4px;
  vertical-align: middle;
  text-align: right;
  flex-shrink: 0;
  line-height: 1;
}

.promotix-lowest-price-pill {
  display: inline-block;
  margin: 0;
  padding: 2px 6px;
  border-radius: 999px;
  background: var(--colorSuccess, #2ecc71);
  color: var(--colorWhite, #fff);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.03em;
  line-height: 1.2;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
}

@media (max-width: 991px) {
  .promotix-lowest-price-slot {
    width: 68px;
    margin-right: 2px;
  }

  .promotix-lowest-price-pill {
    font-size: 8px;
    padding: 2px 5px;
  }
}
`;
